import {
    Box,
    Card,
    CardContent,
    CircularProgress,
    Divider,
    IconButton,
    Skeleton,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Drawer,
    TextField,
} from "@mui/material";
import {
    ArrowLeft,
    Check,
    ClipboardCheck,
    CreditCard,
    DollarSign,
    Download,
    FileText,
    Layers,
    Link2,
    MessageCircle,
    Package,
    Pencil,
    Play,
    RotateCcw,
    Save,
    Send,
    Undo2,
    Wrench,
    X,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import { formatPhone } from '../../common/formatters';
import { httpClient } from "../../common/httpclient";
import { standaloneFieldSx } from '../../common/fieldStyles';
import { messageFromApiErrorBody } from '../../common/apiErrorMessage';
import PageScaffold from "../../component/pageScaffold/PageScaffold";
import StatusChip from "../../component/common/StatusChip";
import OrderData from "./orderData";
import PaymentModal from "./paymentmodal/PaymentModal";
import { downloadServiceOrderPDF } from "./Printer";
import { getEnterpriseIdFromToken, useEnterpriseConfigContext } from '../../component/context/EnterpriseConfigContext';
import { usePermissions } from '../../hooks/usePermissions';
import { useIsMobileShell } from '../../hooks/useIsMobileShell';
import MobileOrderDetail from '../../component/serviceorder/MobileOrderDetail';
import { orderMoment } from '../../component/serviceorder/orderMobileUtils';
import dayjs from 'dayjs';
import HeaderStatusSteps from '../../component/common/HeaderStatusSteps';
import DialogForm from '../../component/dialog/DialogForm';
import ChecklistWhatsappDialog from '../../component/dialog/ChecklistWhatsappDialog';

// ============================================================================
// Componentes locais de apresentação — mesmo vocabulário visual já aprovado em
// `Operator.js` (cabeçalho em gradiente, abas Itens/Financeiro/Checklist,
// DESIGN-SYSTEM.md §6.3). Esta tela é a variação somente-leitura: mesma casca,
// sem nenhum controle de edição. Cópia local intencional (mesmo padrão de
// duplicação já usado entre `Operator.js`/`sales/View.js`/`Sale.js`).
// ============================================================================

// Mesmos 5 status reais de `StatusChip domain="orderService"`, mas sempre
// como pill translúcido branco sobre o gradiente.
function HeaderStatusPill({ status }) {
    const label = status === 'Paga' ? 'Faturada' : (status || 'Não Iniciada');
    return (
        <Box sx={{
            display: 'inline-flex',
            alignItems: 'center',
            fontSize: '11.5px',
            fontWeight: 700,
            padding: '5px 11px',
            borderRadius: '20px',
            bgcolor: 'rgba(255,255,255,.22)',
            color: '#fff',
        }}>
            {label}
        </Box>
    );
}

// `.os-actions button` — ghost translúcido sobre o gradiente (só ações de
// leitura/compartilhamento, sem variantes solid/danger de edição).
function HeaderActionButton({ icon: Icon, label, onClick, loading, disabled }) {
    return (
        <Button
            onClick={onClick}
            disabled={disabled}
            startIcon={loading ? <CircularProgress size={13} sx={{ color: 'inherit' }} /> : <Icon size={14} strokeWidth={2} />}
            sx={{
                padding: '9px 14px',
                borderRadius: '10px',
                border: '1px solid rgba(255,255,255,.3)',
                bgcolor: 'rgba(255,255,255,.12)',
                color: '#fff',
                fontSize: '12.5px',
                fontWeight: 700,
                '&:hover': { bgcolor: 'rgba(255,255,255,.22)', borderColor: 'rgba(255,255,255,.3)' },
                '&.Mui-disabled': { color: 'rgba(255,255,255,.6)', opacity: 0.7 },
            }}
        >
            {label}
        </Button>
    );
}

// `.os-meta-grid .mi` — par label/valor (+ sub-linha opcional) do cabeçalho.
function MetaItem({ label, value, sub }) {
    if (!value) return null;
    return (
        <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: '10.5px', opacity: 0.8, textTransform: 'uppercase', letterSpacing: '.05em', mb: '4px', color: '#fff' }}>
                {label}
            </Typography>
            <Typography sx={{ fontSize: '13.5px', fontWeight: 700, color: '#fff' }}>{value}</Typography>
            {sub && <Typography sx={{ fontSize: '11px', color: 'rgba(255,255,255,.75)', mt: '2px' }}>{sub}</Typography>}
        </Box>
    );
}

// `.summary-row` — usado no resumo da ordem (aba Itens) e resumo financeiro
// (aba Financeiro).
function SummaryRow({ label, value, tone, big }) {
    return (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.875 }}>
            <Typography sx={{ fontSize: 13, color: tone === 'danger' ? 'error.main' : 'text.secondary' }}>{label}</Typography>
            <Typography sx={{
                fontSize: big ? 17 : 14,
                fontWeight: big ? 800 : 700,
                color: tone === 'danger' ? 'error.main' : tone === 'success' ? 'success.main' : 'text.primary',
            }}>
                {value}
            </Typography>
        </Box>
    );
}

// `.items-empty`/`.checklist-empty`/`.fin-empty` — dashed-circle + texto
// (DESIGN-SYSTEM.md §5.11). Sem ação — este é o modo leitura.
function EmptyMiniState({ icon: Icon, title, subtitle, size = 64 }) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, py: 4, textAlign: 'center' }}>
            <Box sx={{
                width: size,
                height: size,
                borderRadius: '50%',
                border: '2px dashed',
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'text.disabled',
            }}>
                <Icon size={size >= 70 ? 26 : 22} strokeWidth={1.6} />
            </Box>
            <Typography sx={{ fontSize: '13.5px', fontWeight: 700, color: 'text.secondary' }}>{title}</Typography>
            {subtitle && <Typography sx={{ fontSize: '12px', color: 'text.disabled', maxWidth: 280 }}>{subtitle}</Typography>}
        </Box>
    );
}

// `.pos-item` em modo leitura — sem qty-box/botões de editar/desconto/remover,
// só "Qtd: N" e o total da linha; serviços com tarefas mostram `.task-list`
// inline (sem accordion, sem status por tarefa).
function ViewItemRow({ item, tasks }) {
    const isService = item.type === 'service';
    const Icon = isService ? Wrench : Package;
    const hasTasks = Array.isArray(tasks) && tasks.length > 0;
    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.5,
            p: '13px 10px',
            borderRadius: '12px',
            mb: 0.75,
            border: '1px solid',
            borderColor: 'divider',
            flexWrap: 'wrap',
        }}>
            <Box sx={{
                width: 42,
                height: 42,
                borderRadius: '11px',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: isService ? 'secondary.bg' : 'info.bg',
                color: isService ? 'secondary.main' : 'primary.main',
            }}>
                <Icon size={18} strokeWidth={1.8} />
            </Box>

            <Box sx={{ flex: 1, minWidth: 160 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: 'text.primary' }}>{item.name}</Typography>
                    <StatusChip domain="itemType" value={isService ? 'servico' : 'produto'} />
                </Box>
                <Typography sx={{ fontSize: 11.5, color: 'text.disabled', mt: '2px' }}>
                    R$ {item.amount_unit} · un.
                </Typography>
                {item.type === 'product' && item.item_discount_type ? (
                    <Typography sx={{ fontSize: 11, color: 'error.main', mt: '2px' }}>
                        Desconto: {item.item_discount_type}
                        {item.item_discount_amount ? ` (− R$ ${item.item_discount_amount})` : ''}
                    </Typography>
                ) : null}
                {hasTasks && (
                    <Box sx={{ ml: '2px', mt: 1, pl: 1.5, borderLeft: '2px solid', borderColor: 'divider', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {tasks.map((task) => (
                            <Typography key={task.id} sx={{ fontSize: 11.5, color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                <Box component="span" sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'success.main', flexShrink: 0 }} />
                                {task.task_name}
                            </Typography>
                        ))}
                    </Box>
                )}
            </Box>

            <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>Qtd: {item.quantity}</Typography>
                <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: 'text.primary' }}>R$ {item.amount_total}</Typography>
            </Box>
        </Box>
    );
}

// `.paid-row` — uma forma de pagamento registrada (aba Financeiro), mesmo
// mapeamento de cor/ícone de `Operator.js`/`sales/View.js` `PAYMENT_META`.
const PAYMENT_META = {
    'CRÉDITO': { icon: CreditCard, iconBg: 'secondary.bg', iconFg: 'secondary.main', label: 'Cartão de crédito' },
    'DÉBITO': { icon: CreditCard, iconBg: 'info.bg', iconFg: 'primary.main', label: 'Cartão de débito' },
    'PIXKEY': { icon: DollarSign, iconBg: 'info.bg', iconFg: 'brand2.main', label: 'PIX - Chave' },
    'PIXQRCODE': { icon: DollarSign, iconBg: 'info.bg', iconFg: 'brand2.main', label: 'PIX - QR Code' },
    'DINHEIRO': { icon: DollarSign, iconBg: 'success.bg', iconFg: 'success.main', label: 'Dinheiro' },
};

function PaymentRow({ payment }) {
    const meta = PAYMENT_META[payment.method] || { icon: CreditCard, iconBg: 'background.subtle', iconFg: 'text.disabled', label: payment.method };
    const Icon = meta.icon;
    let extra = null;
    if (payment.method === 'CRÉDITO' && payment.NumberInstallments > 1) {
        extra = `${payment.NumberInstallments}x de R$ ${payment.ValueInstallment}`;
    } else if (payment.method === 'DINHEIRO' && payment.CashChange && payment.CashChange !== '0,00') {
        extra = `Troco: R$ ${payment.CashChange}`;
    }
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: '13px', bgcolor: 'background.subtle', borderRadius: '12px', mb: 1, '&:last-of-type': { mb: 0 } }}>
            <Box sx={{
                width: 34,
                height: 34,
                borderRadius: '9px',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: meta.iconBg,
                color: meta.iconFg,
            }}>
                <Icon size={16} strokeWidth={2} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{meta.label}</Typography>
                {extra && <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>{extra}</Typography>}
            </Box>
            <Typography sx={{ fontWeight: 800, fontSize: 13.5 }}>R$ {payment.amount}</Typography>
        </Box>
    );
}

// Guarda contra a data "zero" (`0001-01-01`) retornada quando a ordem não
// tem data de entrega definida — mesmo tratamento já usado em `List.js`/`Operator.js`.
function formatOrderDateTime(value) {
    if (!value) return null;
    const formatted = new Date(value).toLocaleString('pt-BR', {
        timeZone: 'UTC',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
    if (formatted === '31/12/1, 23:59' || formatted === '31/12/0001, 23:59') return null;
    return formatted;
}

export default function View() {
    const navigate = useNavigate();
    const { id } = useParams();
    const { settings } = useEnterpriseConfigContext();
    const { hasPermission } = usePermissions();
    const orderDataRef = useRef();
    const isMobileShell = useIsMobileShell();

    // Drawer "Editar dados da ordem" — mesmo fluxo de `Operator.js` (`OrderData` + PUT /orderservices/:id).
    const [isForm, setIsForm] = useState(false);

    const [message, setMessage] = useState('');
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [severity, setSeverity] = useState('success');

    const [orderNumber, setOrderNumber] = useState('');
    const [orderState, setOrderState] = useState({
        total: '0,00',
        totalItems: 0,
        total_services: '0,00',
        total_products: '0,00',
        total_product_item_discount: '0,00',
        cart: [],
        status: 'Não Iniciada',
    });
    const [orderDetails, setOrderDetails] = useState({
        customer_name: '',
        customer_contact: '',
        vehicle_name: '',
        vehicle_plate: '',
        vehicle_type: '',
        order_date: null,
        delivery_date: null,
        observations: '',
        defect_reason: '',
        cancel_reason: '',
        budget_id: null,
        budget_number: null,
        technician_name: '',
        vehicle_kilometer: null,
        customer_phone: '',
    });
    const [serviceTasks, setServiceTasks] = useState({}); // { serviceId: [tasks] }

    // Abas Itens/Financeiro/Checklist + filtro Todos/Produtos/Serviços — só
    // exibição, nunca uma ação de edição.
    const [activeTab, setActiveTab] = useState('itens');
    const [itemFilter, setItemFilter] = useState('todos');

    const [financeState, setFinanceState] = useState({ subtotal: '0,00', discount: '0,00', total: '0,00', total_liquid: '0,00' });
    const [payments, setPayments] = useState([]);
    const [checklistData, setChecklistData] = useState(null);

    const [loading, setLoading] = useState(true);
    const [loadingItems, setLoadingItems] = useState(true);
    const [loadingValues, setLoadingValues] = useState(true);
    const [loadingFinance, setLoadingFinance] = useState(true);
    const [loadingPayments, setLoadingPayments] = useState(true);
    const [loadingChecklist, setLoadingChecklist] = useState(true);
    const [downloadingChecklistPDF, setDownloadingChecklistPDF] = useState(false);
    const [downloadingOrderPDF, setDownloadingOrderPDF] = useState(false);
    const [whatsappConnected, setWhatsappConnected] = useState(false);
    const canSendWhatsapp = hasPermission('serviceorder.send');
    const [whatsappDialogOpen, setWhatsappDialogOpen] = useState(false);
    const [checklistWhatsappOpen, setChecklistWhatsappOpen] = useState(false);
    const [whatsappPhone, setWhatsappPhone] = useState('');
    const [sendingWhatsapp, setSendingWhatsapp] = useState(false);
    const [loadingWhatsappPhone, setLoadingWhatsappPhone] = useState(false);

    const fetchOrderBasicData = async (orderIdParam) => {
        try {
            const response = await httpClient.get(`/orderservices/${orderIdParam}`);
            if (response.status === 200) {
                const orderData = response.data.order || response.data;
                setOrderState(prev => ({ ...prev, status: orderData.status }));
                setOrderNumber(orderData.number || '');
                setOrderDetails({
                    customer_name: orderData?.customer_name || '',
                    customer_contact: orderData?.customer_contact || '',
                    vehicle_name: orderData?.vehicle_name || '',
                    vehicle_plate: orderData?.vehicle_plate || '',
                    vehicle_type: orderData?.vehicle_type || '',
                    order_date: orderData?.order_date || null,
                    delivery_date: orderData?.delivery_date || null,
                    observations: orderData?.observations || '',
                    defect_reason: orderData?.defect_reason || '',
                    cancel_reason: orderData?.cancel_reason || '',
                    budget_id: orderData?.budget_id || null,
                    budget_number: orderData?.budget_number || null,
                    technician_name: orderData?.technician_name || '',
                    vehicle_kilometer: orderData?.vehicle_kilometer ?? null,
                    started_at: orderData?.started_at || null,
                    finished_at: orderData?.finished_at || null,
                    billed_at: orderData?.billed_at || null,
                    reversed_at: orderData?.reversed_at || null,
                    // A API devolve `customer_cellphone`; `customer_contact` segue como alternativa (mesma leitura da tela desktop).
                    customer_phone: formatPhone(orderData?.customer_cellphone || orderData?.customer_contact || ''),
                });

                if (response.data.tasks && Array.isArray(response.data.tasks)) {
                    const tasksByServiceId = {};
                    response.data.tasks.forEach((task) => {
                        if (task.service_id) {
                            if (!tasksByServiceId[task.service_id]) {
                                tasksByServiceId[task.service_id] = [];
                            }
                            tasksByServiceId[task.service_id].push(task);
                        }
                    });
                    Object.keys(tasksByServiceId).forEach((serviceId) => {
                        tasksByServiceId[serviceId].sort((a, b) => (a.sequence_order || 0) - (b.sequence_order || 0));
                    });
                    setServiceTasks(tasksByServiceId);
                }
            }
        } catch (error) {
            console.error('Erro ao carregar dados básicos da ordem:', error);
        }
    };

    const fetchItems = async () => {
        try {
            setLoadingItems(true);
            const response = await httpClient.get(`/orderservices/${id}/products`);
            const responseServices = await httpClient.get(`/orderservices/${id}/services`);
            const productsWithType = response.data.map((item) => ({
                ...item,
                type: 'product',
                name: item.name || item.item_name,
                item_discount_type: item.item_discount_type ?? '',
                item_discount_amount: item.item_discount_amount ?? '',
            }));
            const servicesWithType = responseServices.data.map((item) => ({ ...item, type: 'service' }));
            setOrderState(prev => ({ ...prev, cart: [...productsWithType, ...servicesWithType] }));
        } catch (error) {
            console.error('Erro ao carregar itens:', error);
        } finally {
            setLoadingItems(false);
        }
    };

    const fetchValues = async () => {
        try {
            setLoadingValues(true);
            const response = await httpClient.get(`/orderservices/${id}/values`);
            setOrderState(prev => ({
                ...prev,
                total: response.data.amount,
                totalItems: response.data.count_items,
                total_products: response.data.amount_products,
                total_services: response.data.amount_services,
                total_product_item_discount: response.data.total_product_item_discount ?? prev.total_product_item_discount ?? '0,00',
            }));
        } catch (error) {
            console.error('Erro ao carregar valores:', error);
        } finally {
            setLoadingValues(false);
        }
    };

    const fetchFinanceResume = async (orderIdParam) => {
        try {
            setLoadingFinance(true);
            const response = await httpClient.get(`/orderservices/${orderIdParam}/finance-resume`);
            setFinanceState({
                subtotal: response.data.subtotal ?? '0,00',
                discount: response.data.discount ?? '0,00',
                total: response.data.total ?? '0,00',
                total_liquid: response.data.total_liquid ?? '0,00',
            });
        } catch (error) {
            console.error('Erro ao carregar resumo financeiro:', error);
        } finally {
            setLoadingFinance(false);
        }
    };

    const fetchPayments = async (orderIdParam) => {
        try {
            setLoadingPayments(true);
            const response = await httpClient.get(`/orderservices/${orderIdParam}/payments`);
            setPayments(response.data || []);
        } catch (error) {
            console.error('Erro ao carregar pagamentos:', error);
        } finally {
            setLoadingPayments(false);
        }
    };

    const fetchChecklist = async (orderIdParam) => {
        try {
            setLoadingChecklist(true);
            const response = await httpClient.get(`/checklist/service-order/${orderIdParam}`);
            setChecklistData(response.status === 200 && response.data ? response.data : null);
        } catch (error) {
            setChecklistData(null);
        } finally {
            setLoadingChecklist(false);
        }
    };

    useEffect(() => {
        if (id) {
            setLoading(true);
            const promises = [
                fetchOrderBasicData(id),
                fetchItems(),
                fetchValues(),
                fetchFinanceResume(id),
                fetchPayments(id),
            ];
            if (settings?.os_use_checklist) {
                promises.push(fetchChecklist(id));
            } else {
                setLoadingChecklist(false);
            }
            Promise.all(promises).finally(() => setLoading(false));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, settings?.os_use_checklist]);

    const handleEdit = () => setIsForm(true);

    const closeOrderDataSheet = () => setIsForm(false);

    const handleSave = () => {
        if (orderDataRef && orderDataRef.current) {
            orderDataRef.current.submitForm();
        }
    };

    const handleSubmit = (values) => {
        httpClient.put(`/orderservices/${id}`, values).then(async (response) => {
            if (response.status === 204) {
                setMessage('Ordem de serviço salva com sucesso');
                setSeverity('success');
                setOpenSnackbar(true);
                // Fechar o drawer desmonta `OrderData`; a tela principal é recarregada aqui.
                await fetchOrderBasicData(id);
                closeOrderDataSheet();
            }
        }).catch((error) => {
            console.error('Erro ao salvar ordem de serviço:', error);
            setMessage(messageFromApiErrorBody(error.response?.data) || 'Erro ao salvar ordem de serviço');
            setSeverity('error');
            setOpenSnackbar(true);
        });
    };

    const handleDownloadOrderPDF = async () => {
        if (!id) return;
        try {
            setDownloadingOrderPDF(true);
            await downloadServiceOrderPDF(id, false);
        } catch (error) {
            console.error('Erro ao gerar PDF:', error);
            setMessage('Erro ao gerar PDF da ordem de serviço');
            setSeverity('error');
            setOpenSnackbar(true);
        } finally {
            setDownloadingOrderPDF(false);
        }
    };

    // Status da conexão WhatsApp do tenant (GET /whatsapp) — habilita o envio do PDF.
    useEffect(() => {
        if (!canSendWhatsapp) { setWhatsappConnected(false); return undefined; }
        let active = true;
        httpClient.get('/whatsapp')
            .then((response) => { if (active) setWhatsappConnected(response.data?.status === 'connected'); })
            .catch(() => { if (active) setWhatsappConnected(false); });
        return () => { active = false; };
    }, [canSendWhatsapp]);

    const handleOpenWhatsappSend = async () => {
        setWhatsappPhone('');
        setWhatsappDialogOpen(true);
        try {
            setLoadingWhatsappPhone(true);
            const response = await httpClient.get(`/orderservices/${id}/whatsapp/recipient`);
            setWhatsappPhone(formatPhone(response.data?.phone || ''));
        } catch (error) {
            console.error('Erro ao buscar celular do cliente:', error); // usuário ainda pode digitar o número
        } finally {
            setLoadingWhatsappPhone(false);
        }
    };

    const handleSendOrderPdfWhatsapp = async () => {
        if (!id) return;
        try {
            setSendingWhatsapp(true);
            await httpClient.post(`/orderservices/${id}/whatsapp/send`, { phone: whatsappPhone });
            setWhatsappDialogOpen(false);
            setMessage('Ordem de serviço enviada por WhatsApp');
            setSeverity('success');
        } catch (error) {
            console.error('Erro ao enviar ordem por WhatsApp:', error);
            const data = error.response?.data;
            setMessage((typeof data === 'string' && data.trim()) || messageFromApiErrorBody(data) || 'Erro ao enviar a ordem por WhatsApp');
            setSeverity('error');
        } finally {
            setSendingWhatsapp(false);
            setOpenSnackbar(true);
        }
    };

    const buildPublicOrderLink = () => {
        const enterpriseId = getEnterpriseIdFromToken();
        if (!orderNumber || !enterpriseId) return null;
        return `${window.location.origin}/ordem/${orderNumber}?t=${encodeURIComponent(btoa(enterpriseId))}`;
    };

    const handleCopyOrderLink = async () => {
        const link = buildPublicOrderLink();
        if (!link) {
            setMessage('Não foi possível gerar o link da ordem de serviço');
            setSeverity('error');
            setOpenSnackbar(true);
            return;
        }
        try {
            await navigator.clipboard.writeText(link);
            setMessage('Link da ordem copiado');
            setSeverity('success');
        } catch (error) {
            console.error('Erro ao copiar link da ordem:', error);
            setMessage('Erro ao copiar o link da ordem de serviço');
            setSeverity('error');
        }
        setOpenSnackbar(true);
    };

    const handleShareOrderWhatsApp = () => {
        const link = buildPublicOrderLink();
        if (!link) {
            setMessage('Não foi possível gerar o link da ordem de serviço');
            setSeverity('error');
            setOpenSnackbar(true);
            return;
        }
        const digits = String(orderDetails.customer_contact || orderDetails.customer_phone || '').replace(/\D/g, '');
        const phone = digits && !(digits.startsWith('55') && digits.length > 11) ? `55${digits}` : digits;
        const text = encodeURIComponent(`Olá! Segue o acompanhamento da sua Ordem de Serviço Nº ${orderNumber}: ${link}`);
        window.open(`https://wa.me/${phone}?text=${text}`, '_blank', 'noopener,noreferrer');
    };

    const downloadChecklistPDF = async () => {
        if (!id || !checklistData) return;
        const checklistId = checklistData.checklist_id || checklistData.id;
        if (!checklistId) return;
        try {
            setDownloadingChecklistPDF(true);
            const response = await httpClient.get(`/checklist/service-order/pdf`, {
                params: { checklistId, orderServiceId: id },
                responseType: 'blob',
            });
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const urlBlob = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = urlBlob;
            link.download = `checklist_os_${orderNumber || id}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(urlBlob);
        } catch (error) {
            console.error('Erro ao baixar PDF do checklist:', error);
            setMessage('Erro ao baixar PDF do checklist');
            setSeverity('error');
            setOpenSnackbar(true);
        } finally {
            setDownloadingChecklistPDF(false);
        }
    };

    const breadcrumbs = [
        { title: 'Ordens de Serviço', link: '/serviceorders' },
    ];

    const orderDatePedido = formatOrderDateTime(orderDetails.order_date);
    const orderDataEntrega = formatOrderDateTime(orderDetails.delivery_date);
    const hasProductItemDiscount = orderState.total_product_item_discount && orderState.total_product_item_discount !== '0,00';
    const filteredCart = orderState.cart.filter((item) => itemFilter === 'todos' || item.type === itemFilter);
    const isFinalizada = orderState.status === 'Finalizada';

    // Etapas do status (status + data/hora) — mesma regra do modo mobile (MobileOrderDetail).
    const statusKey = String(orderState.status || '').trim().toLowerCase();
    const stepStarted = ['em execução', 'iniciada', 'finalizada', 'paga', 'faturada', 'estornada'].includes(statusKey);
    const stepFinished = ['finalizada', 'paga', 'faturada', 'estornada'].includes(statusKey);
    const stepBilled = ['paga', 'faturada'].includes(statusKey);
    const stepStamp = (value) => {
        const m = value ? dayjs(value) : null;
        return m && m.isValid() && m.year() >= 1900 ? `${m.format('DD/MM')} · ${m.format('HH:mm')}` : undefined;
    };
    const stepCreated = orderMoment(orderDetails.order_date);
    const statusSteps = [{ title: 'OS criada', sub: stepCreated ? `${stepCreated.format('DD/MM')} · ${stepCreated.format('HH:mm')}` : undefined, state: 'done' }];
    if (statusKey === 'cancelada') {
        statusSteps.push({ title: 'OS cancelada', sub: orderDetails.cancel_reason || undefined, state: 'done' });
    } else {
        statusSteps.push({ title: 'Iniciada', sub: stepStarted ? stepStamp(orderDetails.started_at) : '—', state: stepStarted ? 'done' : 'off' });
        statusSteps.push({ title: 'Finalização', sub: stepFinished ? stepStamp(orderDetails.finished_at) : '—', state: stepFinished ? 'done' : 'off' });
        if (stepBilled) statusSteps.push({ title: 'Faturada', sub: stepStamp(orderDetails.billed_at), state: 'done' });
        if (statusKey === 'estornada') statusSteps.push({ title: 'OS estornada', sub: [stepStamp(orderDetails.reversed_at), orderDetails.cancel_reason ? `Motivo: ${orderDetails.cancel_reason}` : null].filter(Boolean).join(' · ') || undefined, state: 'done' });
    }

    // Cabeçalho: "Editar ordem" só aparece com as mesmas regras de `Operator.js`
    // (permissão PUT e status Não Iniciada/Em Execução — Finalizada, Faturada e
    // Cancelada não editam). "Copiar link" e "WhatsApp" compartilham a página
    // pública `/ordem/:numero` (além do "Baixar PDF" do protótipo
    // `prototipo-visualizacao-ordem.html`).
    const isEditableStatus = orderState.status === 'Não Iniciada' || orderState.status === 'Em Execução';
    const canEditOrder = hasPermission('serviceorder.update') && isEditableStatus;
    // "Iniciar": mesma chamada e permissão da lista/Operator (POST /orderservices/:id/execute, `serviceorder.execute`).
    const canStartOrder = hasPermission('serviceorder.execute') && orderState.status === 'Não Iniciada';
    const [openStartDialog, setOpenStartDialog] = useState(false);
    const [startingOrder, setStartingOrder] = useState(false);

    const handleStartOrder = async () => {
        if (startingOrder) return;
        setStartingOrder(true);
        try {
            const response = await httpClient.post(`/orderservices/${id}/execute`);
            // A API pode responder 200 com `{ error }` no corpo quando a execução falha.
            if (response.status === 200 && !response.data?.error) {
                // Mesmo padrão de List.js: após iniciar, abre a tela do operador (sem snackbar, a tela desmonta).
                setOpenStartDialog(false);
                navigate(`/serviceorder/${id}/operator`);
            } else {
                setOpenStartDialog(false);
                setMessage(
                    (typeof response.data === 'string' && response.data.trim())
                    || response.data?.error
                    || messageFromApiErrorBody(response.data)
                    || 'Erro ao iniciar ordem de serviço'
                );
                setSeverity('error');
                setOpenSnackbar(true);
            }
        } catch (error) {
            setOpenStartDialog(false);
            setMessage(messageFromApiErrorBody(error.response?.data) || 'Erro ao iniciar ordem de serviço');
            setSeverity('error');
            setOpenSnackbar(true);
        } finally {
            setStartingOrder(false);
        }
    };

    // "Finalizar": mesma chamada do Operator/List (POST /orderservices/:id/finish, `serviceorder.execute`), só em "Em Execução".
    const canFinishOrder = hasPermission('serviceorder.execute') && orderState.status === 'Em Execução';
    const [openFinishDialog, setOpenFinishDialog] = useState(false);
    const [finishingOrder, setFinishingOrder] = useState(false);

    const handleFinishOrder = async () => {
        if (finishingOrder) return;
        setFinishingOrder(true);
        try {
            const response = await httpClient.post(`/orderservices/${id}/finish`);
            setOpenFinishDialog(false);
            // A API pode responder 200 com `{ error }` no corpo; regras de negócio voltam como 422 `{ error }`.
            if (response.status === 200 && !response.data?.error) {
                setMessage('Ordem de serviço finalizada com sucesso');
                setSeverity('success');
                setOpenSnackbar(true);
                await fetchOrderBasicData(id);
            } else {
                setMessage(
                    (typeof response.data === 'string' && response.data.trim())
                    || response.data?.error
                    || messageFromApiErrorBody(response.data)
                    || 'Erro ao finalizar ordem de serviço'
                );
                setSeverity('error');
                setOpenSnackbar(true);
            }
        } catch (error) {
            setOpenFinishDialog(false);
            setMessage(messageFromApiErrorBody(error.response?.data) || 'Erro ao finalizar ordem de serviço');
            setSeverity('error');
            setOpenSnackbar(true);
        } finally {
            setFinishingOrder(false);
        }
    };

    // "Reabrir": POST /orderservices/:id/reopen (`serviceorder.execute`), só em "Finalizada"; volta para "Em Execução".
    const canReopenOrder = hasPermission('serviceorder.execute') && orderState.status === 'Finalizada';
    const [openReopenDialog, setOpenReopenDialog] = useState(false);
    const [reopeningOrder, setReopeningOrder] = useState(false);

    const handleReopenOrder = async () => {
        if (reopeningOrder) return;
        setReopeningOrder(true);
        try {
            await httpClient.post(`/orderservices/${id}/reopen`);
            setOpenReopenDialog(false);
            navigate(`/serviceorder/${id}/operator`);
        } catch (error) {
            setOpenReopenDialog(false);
            setMessage(messageFromApiErrorBody(error.response?.data) || 'Erro ao reabrir ordem de serviço');
            setSeverity('error');
            setOpenSnackbar(true);
        } finally {
            setReopeningOrder(false);
        }
    };

    // "Faturar": abre o mesmo PaymentModal do Operator (`serviceorder.bill`), só em "Finalizada".
    const canBillOrder = hasPermission('serviceorder.bill') && orderState.status === 'Finalizada';
    const [openPaymentModal, setOpenPaymentModal] = useState(false);

    const handlePaymentFinish = async () => {
        setOpenPaymentModal(false);
        setMessage('Ordem de serviço faturada com sucesso!');
        setSeverity('success');
        setOpenSnackbar(true);
        await Promise.all([fetchOrderBasicData(id), fetchFinanceResume(id), fetchPayments(id)]);
    };

    // "Estornar": POST /orderservices/:id/reverse {reason} (`serviceorder.reverse`), só em "Paga" (faturada). Mesmo fluxo da List.
    const canReverseOrder = hasPermission('serviceorder.reverse') && orderState.status === 'Paga';
    const [openReverseDialog, setOpenReverseDialog] = useState(false);
    const [reversingOrder, setReversingOrder] = useState(false);
    const [reverseReason, setReverseReason] = useState('');
    const [reverseReasonError, setReverseReasonError] = useState('');

    const handleReverseOrder = async () => {
        if (reversingOrder) return;
        const reason = reverseReason.trim();
        if (!reason) {
            setReverseReasonError('Informe o motivo do estorno.');
            return;
        }
        setReverseReasonError('');
        setReversingOrder(true);
        try {
            const response = await httpClient.post(`/orderservices/${id}/reverse`, { reason });
            if (response.status === 200) {
                setOpenReverseDialog(false);
                setMessage('Ordem de serviço estornada com sucesso');
                setSeverity('success');
                setOpenSnackbar(true);
                await Promise.all([fetchOrderBasicData(id), fetchFinanceResume(id), fetchPayments(id)]);
            } else {
                // validateStatus do httpClient aceita 4xx/5xx: o erro vem em response.data.
                setReverseReasonError(
                    (typeof response.data === 'string' && response.data.trim())
                    || messageFromApiErrorBody(response.data)
                    || 'Erro ao estornar ordem de serviço'
                );
            }
        } catch (error) {
            setReverseReasonError(messageFromApiErrorBody(error.response?.data) || 'Erro ao estornar ordem de serviço');
        } finally {
            setReversingOrder(false);
        }
    };

    const headerActions = [
        ...(canStartOrder ? [{ key: 'start', label: 'Iniciar', icon: Play, onClick: () => setOpenStartDialog(true), loading: startingOrder, disabled: startingOrder }] : []),
        ...(canFinishOrder ? [{ key: 'finish', label: 'Finalizar', icon: Check, onClick: () => setOpenFinishDialog(true), loading: finishingOrder, disabled: finishingOrder }] : []),
        ...(canReopenOrder ? [{ key: 'reopen', label: 'Reabrir', icon: RotateCcw, onClick: () => setOpenReopenDialog(true), loading: reopeningOrder, disabled: reopeningOrder }] : []),
        ...(canBillOrder ? [{ key: 'bill', label: 'Faturar', icon: CreditCard, onClick: () => setOpenPaymentModal(true) }] : []),
        ...(canReverseOrder ? [{ key: 'reverse', label: 'Estornar', icon: Undo2, onClick: () => { setReverseReason(''); setReverseReasonError(''); setOpenReverseDialog(true); }, loading: reversingOrder, disabled: reversingOrder }] : []),
        ...(canEditOrder ? [{ key: 'edit', label: 'Editar ordem', icon: Pencil, onClick: handleEdit }] : []),
        { key: 'pdf', label: 'Baixar PDF', icon: Download, onClick: handleDownloadOrderPDF, loading: downloadingOrderPDF, disabled: downloadingOrderPDF },
        { key: 'copy-link', label: 'Copiar link', icon: Link2, onClick: handleCopyOrderLink },
        // { key: 'whatsapp', label: 'WhatsApp', icon: MessageCircle, onClick: handleShareOrderWhatsApp },
        ...(canSendWhatsapp ? [{
            key: 'whatsapp-send',
            label: 'Enviar por WhatsApp',
            icon: Send,
            onClick: handleOpenWhatsappSend,
            disabled: !whatsappConnected,
            tooltip: whatsappConnected ? '' : 'WhatsApp não conectado. Conecte o número em Configurações > WhatsApp.',
        }] : []),
    ];

    // Mobile: excluir a ordem (a ação existia só na lista; no cartão mobile ela vive nas "Ações" do detalhe).
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [deletingOrder, setDeletingOrder] = useState(false);
    const canDeleteOrder = hasPermission('serviceorder.delete') && orderState.status !== 'Paga';
    const canOperateOrder = (orderState.status === 'Não Iniciada' || orderState.status === 'Em Execução') && hasPermission('serviceorder.execute');

    const handleDeleteOrder = () => {
        setDeletingOrder(true);
        httpClient.delete(`/orderservices/${id}`).then((response) => {
            setDeletingOrder(false);
            setOpenDeleteDialog(false);
            if (response.status === 200) {
                navigate('/serviceorders', { replace: true });
                return;
            }
            // validateStatus do httpClient aceita 4xx/5xx: o erro vem em response.data.
            setSeverity('error');
            setMessage((typeof response.data === 'string' && response.data.trim()) || messageFromApiErrorBody(response.data) || 'Erro ao excluir ordem de serviço');
            setOpenSnackbar(true);
        }).catch(() => {
            setDeletingOrder(false);
            setOpenDeleteDialog(false);
            setSeverity('error');
            setMessage('Erro ao excluir ordem de serviço');
            setOpenSnackbar(true);
        });
    };

    return (
        <PageScaffold
            title="Ordem de Serviço"
            links={breadcrumbs}
            openSnackbar={openSnackbar}
            message={message}
            setOpenSnackbar={setOpenSnackbar}
            severity={severity}
        >
            <Box sx={{ width: '100%', maxWidth: 1400, mx: 'auto' }}>
                {isMobileShell ? (
                    <MobileOrderDetail
                        loading={loading}
                        orderNumber={orderNumber}
                        status={orderState.status}
                        details={orderDetails}
                        usesVehicle={Boolean(settings?.os_use_vehicle)}
                        items={orderState.cart}
                        serviceTasks={serviceTasks}
                        loadingItems={loadingItems}
                        finance={financeState}
                        payments={payments}
                        checklist={checklistData}
                        checklistEnabled={Boolean(settings?.os_use_checklist)}
                        canEdit={canEditOrder}
                        canDelete={canDeleteOrder}
                        canSendWhatsapp={canSendWhatsapp}
                        whatsappConnected={whatsappConnected}
                        canOperate={canOperateOrder}
                        printing={downloadingOrderPDF}
                        onPrint={handleDownloadOrderPDF}
                        onOperate={() => navigate(`/serviceorder/${id}/operator`)}
                        onEdit={handleEdit}
                        onCopyLink={handleCopyOrderLink}
                        onSendWhatsapp={handleOpenWhatsappSend}
                        onShareWhatsapp={handleShareOrderWhatsApp}
                        onDelete={() => setOpenDeleteDialog(true)}
                        onOpenQuote={() => navigate(`/servicequotes/${orderDetails.budget_id}/operator`)}
                        onChecklistPdf={downloadChecklistPDF}
                        downloadingChecklistPdf={downloadingChecklistPDF}
                        onChecklistWhatsapp={() => setChecklistWhatsappOpen(true)}
                    />
                ) : (
                <>
                {/* CABEÇALHO — os-header (gradiente de marca, §2.4/§6.3) */}
                {loading ? (
                    <Card elevation={0} sx={{ borderRadius: '20px', mb: 2.25, p: '22px 24px', background: (t) => t.palette.gradient.brand, boxShadow: (t) => t.customShadows.md }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.25, flexWrap: 'wrap' }}>
                            <Skeleton variant="circular" width={36} height={36} sx={{ bgcolor: 'rgba(255,255,255,.25)' }} />
                            <Skeleton variant="text" width={200} height={34} sx={{ bgcolor: 'rgba(255,255,255,.25)' }} />
                        </Box>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(5, 1fr)' }, gap: 2.25 }}>
                            {[...Array(5)].map((_, i) => (
                                <Skeleton key={i} variant="text" width={90} height={36} sx={{ bgcolor: 'rgba(255,255,255,.2)' }} />
                            ))}
                        </Box>
                    </Card>
                ) : (
                    <Card elevation={0} sx={{ borderRadius: '20px', mb: 2.25, p: '22px 24px', background: (t) => t.palette.gradient.brand, boxShadow: (t) => t.customShadows.md }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 2.25 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                                {!isMobileShell && <Tooltip title="Voltar">
                                    <IconButton
                                        onClick={() => (window.history.state?.idx > 0 ? navigate(-1) : navigate('/serviceorders'))}
                                        sx={{
                                            width: 36,
                                            height: 36,
                                            borderRadius: '10px',
                                            bgcolor: 'rgba(255,255,255,.15)',
                                            border: '1px solid rgba(255,255,255,.25)',
                                            color: '#fff',
                                            '&:hover': { bgcolor: 'rgba(255,255,255,.25)' },
                                        }}
                                    >
                                        <ArrowLeft size={16} strokeWidth={2} />
                                    </IconButton>
                                </Tooltip>}
                                <Typography component="h1" sx={{ fontSize: '21px', fontWeight: 800, letterSpacing: '-.01em', color: '#fff' }}>
                                    ORDEM Nº {orderNumber}
                                </Typography>
                                <HeaderStatusPill status={orderState.status} />
                            </Box>

                            {!isMobileShell && <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                {headerActions.map(({ key, tooltip, ...action }) => (
                                    tooltip ? (
                                        <Tooltip key={key} title={tooltip}>
                                            <span><HeaderActionButton {...action} /></span>
                                        </Tooltip>
                                    ) : (
                                        <HeaderActionButton key={key} {...action} />
                                    )
                                ))}
                            </Box>}
                        </Box>

                        <Box sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)', lg: 'repeat(5, 1fr)' },
                            gap: 2.25,
                        }}>
                            <MetaItem label="Data do pedido" value={orderDatePedido} />
                            <MetaItem label="Data de entrega" value={orderDataEntrega} />
                            <MetaItem
                                label="Cliente"
                                value={orderDetails.customer_name}
                                sub={orderDetails.customer_contact ? formatPhone(orderDetails.customer_contact) : null}
                            />
                            {settings?.os_use_vehicle && (
                                <MetaItem
                                    label="Veículo"
                                    value={orderDetails.vehicle_name}
                                    sub={
                                        orderDetails.vehicle_plate && orderDetails.vehicle_type !== 'bicycle'
                                            ? `Placa ${orderDetails.vehicle_plate}`
                                            : null
                                    }
                                />
                            )}
                            <MetaItem label="Motivo/Defeito" value={orderDetails.defect_reason} />
                            <MetaItem label="Observações" value={orderDetails.observations} />
                            {orderState.status === 'Cancelada' && (
                                <MetaItem label="Motivo do cancelamento" value={orderDetails.cancel_reason} />
                            )}
                        </Box>

                        {/* ETAPAS DO STATUS (status + hora), em linha, igual ao mobile */}
                        <Box sx={{ mt: 3, p: '14px 18px', borderRadius: '14px', bgcolor: 'rgba(255,255,255,.12)', border: '1px solid rgba(255,255,255,.18)' }}>
                            <HeaderStatusSteps steps={statusSteps} />
                        </Box>

                        {orderDetails.budget_id && orderDetails.budget_number && (
                            <Box
                                component={RouterLink}
                                to={`/servicequotes/${orderDetails.budget_id}/operator`}
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 0.75,
                                    fontSize: '11.5px',
                                    fontWeight: 700,
                                    padding: '5px 11px',
                                    borderRadius: '20px',
                                    bgcolor: 'rgba(255,255,255,.15)',
                                    color: '#fff',
                                    mt: 1.75,
                                    textDecoration: 'none',
                                    width: 'fit-content',
                                    '&:hover': { bgcolor: 'rgba(255,255,255,.25)' },
                                }}
                            >
                                <FileText size={12} strokeWidth={2} />
                                Gerada a partir do Orçamento #{orderDetails.budget_number}
                            </Box>
                        )}
                    </Card>
                )}

                {/* ABAS — seg-tabs Itens/Financeiro/Checklist */}
                <ToggleButtonGroup
                    exclusive
                    value={activeTab}
                    onChange={(e, v) => v && setActiveTab(v)}
                    sx={{
                        mb: 2.25,
                        width: isMobileShell ? '100%' : 'fit-content',
                        // Mobile: abas dividem a linha, sem quebra de texto.
                        '& .MuiToggleButton-root': isMobileShell
                            ? { flex: '1 1 0', minWidth: 0, px: 0.75, gap: 0.5, fontSize: 12.5, whiteSpace: 'nowrap', lineHeight: 1.2 }
                            : undefined,
                    }}
                >
                    <ToggleButton value="itens" sx={{ gap: 0.75, px: 2.25 }}>
                        <Layers size={14} strokeWidth={2} /> {isMobileShell ? 'Itens' : 'Itens da ordem'}
                    </ToggleButton>
                    <ToggleButton value="financeiro" sx={{ gap: 0.75, px: 2.25 }}>
                        <DollarSign size={14} strokeWidth={2} /> Financeiro
                    </ToggleButton>
                    {settings?.os_use_checklist && (
                        <ToggleButton value="checklist" sx={{ gap: 0.75, px: 2.25 }}>
                            <ClipboardCheck size={14} strokeWidth={2} /> Checklist
                        </ToggleButton>
                    )}
                </ToggleButtonGroup>

                {/* ABA: ITENS (somente leitura — sem busca, sem qty +/-, sem editar/desconto/remover) */}
                {activeTab === 'itens' && (
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1.7fr 1fr' }, gap: 2, alignItems: 'flex-start' }}>
                        <Card elevation={0}>
                            <CardContent sx={{ p: 2 }}>
                                <ToggleButtonGroup
                                    exclusive
                                    size="small"
                                    value={itemFilter}
                                    onChange={(e, v) => v && setItemFilter(v)}
                                    sx={{ mb: 2, ...(isMobileShell && { width: '100%', '& .MuiToggleButton-root': { flex: '1 1 0', minWidth: 0, whiteSpace: 'nowrap' } }) }}
                                >
                                    <ToggleButton value="todos">{isMobileShell ? 'Todos' : 'Todos os itens'}</ToggleButton>
                                    <ToggleButton value="product">{isMobileShell ? 'Produtos' : 'Apenas produtos'}</ToggleButton>
                                    <ToggleButton value="service">{isMobileShell ? 'Serviços' : 'Apenas serviços'}</ToggleButton>
                                </ToggleButtonGroup>

                                <Box sx={{ maxHeight: 560, overflowY: 'auto', pr: 0.5 }}>
                                    {loadingItems ? (
                                        <Stack spacing={1}>
                                            {[1, 2, 3].map((n) => (
                                                <Skeleton key={n} variant="rounded" height={68} sx={{ borderRadius: '12px' }} />
                                            ))}
                                        </Stack>
                                    ) : filteredCart.length === 0 ? (
                                        <EmptyMiniState
                                            icon={Layers}
                                            title="Nenhum item nesta ordem"
                                            subtitle="Esta ordem não possui itens registrados"
                                        />
                                    ) : (
                                        filteredCart.map((item) => {
                                            const serviceId = item.service_id || item.id;
                                            const tasks = item.type === 'service' ? serviceTasks[serviceId] : null;
                                            return <ViewItemRow key={item.id} item={item} tasks={tasks} />;
                                        })
                                    )}
                                </Box>
                            </CardContent>
                        </Card>

                        <Card elevation={0} sx={{ position: 'sticky', top: 20 }}>
                            <CardContent sx={{ p: 2.5 }}>
                                <Typography sx={{ fontSize: 13.5, fontWeight: 700, mb: 1.5 }}>Resumo da ordem</Typography>
                                {loadingValues ? (
                                    <Stack spacing={1}>
                                        {[1, 2, 3, 4].map((n) => <Skeleton key={n} variant="text" height={28} />)}
                                    </Stack>
                                ) : (
                                    <>
                                        <SummaryRow label="Total de itens" value={orderState.totalItems} />
                                        <SummaryRow label="Produtos" value={`R$ ${orderState.total_products}`} />
                                        {hasProductItemDiscount && (
                                            <SummaryRow label="Desconto nos produtos" value={`− R$ ${orderState.total_product_item_discount}`} tone="danger" />
                                        )}
                                        <SummaryRow label="Serviços" value={`R$ ${orderState.total_services}`} />
                                        <Divider sx={{ my: 1 }} />
                                        <SummaryRow label="Total" value={`R$ ${orderState.total}`} tone="success" big />
                                    </>
                                )}
                            </CardContent>
                        </Card>
                    </Box>
                )}

                {/* ABA: FINANCEIRO (já somente leitura, reaproveitada sem alterações) */}
                {activeTab === 'financeiro' && (
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                        <Card elevation={0}>
                            <CardContent sx={{ p: 2.5 }}>
                                <Typography sx={{ fontSize: 13.5, fontWeight: 700, mb: 1.5 }}>Resumo financeiro</Typography>
                                {loadingFinance ? (
                                    <Stack spacing={1}>
                                        {[1, 2, 3, 4].map((n) => <Skeleton key={n} variant="text" height={28} />)}
                                    </Stack>
                                ) : (
                                    <>
                                        <SummaryRow label="Subtotal" value={`R$ ${financeState.subtotal}`} />
                                        <SummaryRow label="Desconto" value={`− R$ ${financeState.discount}`} tone="danger" />
                                        <SummaryRow label="Total" value={`R$ ${financeState.total}`} />
                                        <Divider sx={{ my: 1 }} />
                                        <SummaryRow label="Total líquido" value={`R$ ${financeState.total_liquid}`} tone="success" big />
                                    </>
                                )}
                            </CardContent>
                        </Card>

                        <Card elevation={0}>
                            <CardContent sx={{ p: 2.5 }}>
                                <Typography sx={{ fontSize: 13.5, fontWeight: 700, mb: 1.5 }}>Formas de pagamento</Typography>
                                {loadingPayments ? (
                                    <Stack spacing={1}>
                                        {[1, 2].map((n) => <Skeleton key={n} variant="rounded" height={62} sx={{ borderRadius: '12px' }} />)}
                                    </Stack>
                                ) : payments.length === 0 ? (
                                    <EmptyMiniState
                                        icon={CreditCard}
                                        title="Nenhum pagamento registrado ainda"
                                        subtitle={isFinalizada ? 'A ordem ainda não foi faturada.' : 'Nenhum pagamento registrado para esta ordem.'}
                                    />
                                ) : (
                                    <Box>
                                        {payments.map((payment, index) => (
                                            <PaymentRow key={index} payment={payment} />
                                        ))}
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    </Box>
                )}

                {/* ABA: CHECKLIST (somente leitura — sem "Preencher checklist" no estado vazio) */}
                {activeTab === 'checklist' && settings?.os_use_checklist && (
                    <Box>
                        {loadingChecklist ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                <CircularProgress size={28} />
                            </Box>
                        ) : checklistData ? (
                            <Card elevation={0}>
                                <CardContent sx={{ p: 2.5 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                                        <Box>
                                            <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{checklistData.checklist_name}</Typography>
                                            <Typography sx={{ fontSize: 12, color: 'text.disabled', mt: 0.5 }}>
                                                {checklistData.mechanic_name && <>Preenchido por: <b>{checklistData.mechanic_name}</b></>}
                                                {checklistData.completion_date && ` · ${new Date(checklistData.completion_date).toLocaleDateString('pt-BR')}`}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                startIcon={downloadingChecklistPDF ? <CircularProgress size={13} /> : <Download size={14} strokeWidth={2} />}
                                                onClick={downloadChecklistPDF}
                                                disabled={downloadingChecklistPDF}
                                            >
                                                Baixar PDF
                                            </Button>
                                            {canSendWhatsapp && (
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    startIcon={<Send size={14} strokeWidth={2} />}
                                                    onClick={() => setChecklistWhatsappOpen(true)}
                                                    disabled={!whatsappConnected}
                                                    title={whatsappConnected ? '' : 'WhatsApp não conectado. Conecte o número em Configurações > WhatsApp.'}
                                                >
                                                    Enviar por WhatsApp
                                                </Button>
                                            )}
                                        </Box>
                                    </Box>

                                    {checklistData.evaluations && checklistData.evaluations.length > 0 ? (
                                        Object.values(
                                            checklistData.evaluations.reduce((acc, evaluation) => {
                                                const groupId = evaluation.group_id || 'other';
                                                if (!acc[groupId]) {
                                                    acc[groupId] = { group_id: groupId, group_name: evaluation.group_name || 'Outros', evaluations: [] };
                                                }
                                                acc[groupId].evaluations.push(evaluation);
                                                return acc;
                                            }, {})
                                        ).map((group) => (
                                            <Box key={group.group_id} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '14px', mb: 1.75, overflow: 'hidden' }}>
                                                <Box sx={{
                                                    p: '13px 16px',
                                                    background: (t) => t.palette.gradient.brand,
                                                    color: '#fff',
                                                    fontSize: 13,
                                                    fontWeight: 800,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                }}>
                                                    <ClipboardCheck size={14} strokeWidth={2} /> {group.group_name}
                                                </Box>
                                                <Box>
                                                    {group.evaluations.map((evaluation, index) => (
                                                        <Box
                                                            key={evaluation.evaluation_id || index}
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 1.5,
                                                                p: '12px 16px',
                                                                borderBottom: index < group.evaluations.length - 1 ? '1px solid' : 'none',
                                                                borderColor: 'divider',
                                                                flexWrap: 'wrap',
                                                            }}
                                                        >
                                                            <Typography sx={{ flex: 1, minWidth: 160, fontSize: 13, fontWeight: 600 }}>
                                                                {evaluation.item_name}
                                                            </Typography>
                                                            <StatusChip domain="checklist" value={evaluation.answer_description} />
                                                            {evaluation.observation && (
                                                                <Typography sx={{ width: '100%', fontSize: 11.5, fontStyle: 'italic', color: 'text.secondary', borderLeft: '2px solid', borderColor: 'divider', pl: 1.25 }}>
                                                                    {evaluation.observation}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    ))}
                                                </Box>
                                            </Box>
                                        ))
                                    ) : (
                                        <Typography sx={{ fontSize: 13, color: 'text.secondary', textAlign: 'center', py: 3 }}>
                                            Nenhuma avaliação encontrada
                                        </Typography>
                                    )}
                                </CardContent>
                            </Card>
                        ) : (
                            <Card elevation={0}>
                                <CardContent sx={{ p: 2.5 }}>
                                    <EmptyMiniState
                                        icon={ClipboardCheck}
                                        title="Nenhum checklist preenchido"
                                        subtitle="Esta ordem não possui checklist de inspeção registrado."
                                        size={70}
                                    />
                                </CardContent>
                            </Card>
                        )}
                    </Box>
                )}

                </>
                )}

                <DialogForm
                    iconType="warning"
                    open={openStartDialog}
                    title="Atenção"
                    confirmButtonText="Sim, Iniciar"
                    content="Tem certeza que deseja iniciar a execução da ordem de serviço?"
                    secondaryContent="Esta ação não pode ser desfeita. A ordem de serviço será iniciada."
                    onClose={() => { if (!startingOrder) setOpenStartDialog(false); }}
                    onSubmit={handleStartOrder}
                    loading={startingOrder}
                    loadingText="Iniciando..."
                />

                <DialogForm
                    iconType="warning"
                    open={openFinishDialog}
                    title="Atenção"
                    confirmButtonText="Sim, Finalizar"
                    content="Tem certeza que deseja finalizar a ordem de serviço?"
                    onClose={() => { if (!finishingOrder) setOpenFinishDialog(false); }}
                    onSubmit={handleFinishOrder}
                    loading={finishingOrder}
                    loadingText="Finalizando..."
                />

                <DialogForm
                    iconType="warning"
                    open={openReopenDialog}
                    title="Atenção"
                    confirmButtonText="Sim, Reabrir"
                    content="Tem certeza que deseja reabrir a ordem de serviço? Ela voltará para o status Em Execução."
                    onClose={() => { if (!reopeningOrder) setOpenReopenDialog(false); }}
                    onSubmit={handleReopenOrder}
                    loading={reopeningOrder}
                    loadingText="Reabrindo..."
                />

                <DialogForm
                    iconType="warning"
                    open={openReverseDialog}
                    title="Atenção"
                    confirmButtonText="Sim, Estornar"
                    content="Tem certeza que deseja estornar a ordem de serviço?"
                    secondaryContent="Os produtos voltarão ao estoque, o valor será retirado do caixa e do financeiro, e a ordem ficará com o status Estornada. Esta ação não pode ser desfeita."
                    onClose={() => { if (!reversingOrder) setOpenReverseDialog(false); }}
                    onSubmit={handleReverseOrder}
                    loading={reversingOrder}
                    loadingText="Estornando..."
                >
                    <TextField
                        sx={standaloneFieldSx}
                        label="Motivo"
                        fullWidth
                        multiline
                        minRows={3}
                        required
                        autoFocus
                        value={reverseReason}
                        onChange={(e) => {
                            setReverseReason(e.target.value);
                            if (reverseReasonError) setReverseReasonError('');
                        }}
                        error={Boolean(reverseReasonError)}
                        helperText={reverseReasonError}
                        disabled={reversingOrder}
                    />
                </DialogForm>

                {canBillOrder && (
                    <PaymentModal
                        open={openPaymentModal}
                        onClose={() => setOpenPaymentModal(false)}
                        total={orderState.total}
                        onFinish={handlePaymentFinish}
                        entityType="service_order"
                        entityId={id}
                    />
                )}

                <DialogForm
                    iconType="warning"
                    open={openDeleteDialog}
                    title="Atenção"
                    confirmButtonText="Sim, Excluir"
                    content="Tem certeza que deseja excluir a ordem de serviço?"
                    secondaryContent="Esta ação não pode ser desfeita. A ordem de serviço será excluída permanentemente."
                    onClose={() => { if (!deletingOrder) setOpenDeleteDialog(false); }}
                    onSubmit={handleDeleteOrder}
                    loading={deletingOrder}
                    loadingText="Excluindo..."
                />

                {/* MODAL: conferir/editar o número antes de enviar o PDF por WhatsApp */}
                <Dialog open={whatsappDialogOpen} onClose={() => !sendingWhatsapp && setWhatsappDialogOpen(false)} fullWidth maxWidth="xs">
                    <DialogTitle>Enviar por WhatsApp</DialogTitle>
                    <DialogContent>
                        <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 2 }}>
                            O PDF da ordem nº {orderNumber} será enviado ao número abaixo. Confira antes de enviar.
                        </Typography>
                        <TextField
                            label="Telefone (WhatsApp)"
                            helperText={loadingWhatsappPhone ? 'Buscando celular do cliente...' : 'Celular do cliente (editável). DDD + número, ex.: (11) 99999-9999'}
                            fullWidth
                            autoFocus
                            sx={standaloneFieldSx}
                            value={whatsappPhone}
                            onChange={(e) => setWhatsappPhone(e.target.value)}
                        />
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2 }}>
                        <Button onClick={() => setWhatsappDialogOpen(false)} disabled={sendingWhatsapp}>Cancelar</Button>
                        <Button
                            variant="contained"
                            onClick={handleSendOrderPdfWhatsapp}
                            disabled={sendingWhatsapp || !whatsappPhone.trim()}
                            startIcon={sendingWhatsapp ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : <Send size={14} strokeWidth={2} />}
                        >
                            Enviar
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* MODAL: enviar o PDF do checklist por WhatsApp */}
                <ChecklistWhatsappDialog
                    open={checklistWhatsappOpen}
                    serviceOrderId={id}
                    orderNumber={orderNumber}
                    onClose={() => setChecklistWhatsappOpen(false)}
                    onResult={(msg, sev) => { setMessage(msg); setSeverity(sev); setOpenSnackbar(true); }}
                />

                {/* DRAWER: editar dados da ordem (mesmo do Operator.js) */}
                <Drawer
                    anchor="right"
                    open={isForm}
                    onClose={closeOrderDataSheet}
                    slotProps={{
                        backdrop: { sx: { backgroundColor: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)' } },
                    }}
                    PaperProps={{
                        sx: {
                            width: { xs: '100%', sm: 560, md: 640 },
                            maxWidth: '100vw',
                            borderTopLeftRadius: 16,
                            borderBottomLeftRadius: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                        },
                    }}
                >
                    <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>Editar dados da ordem</Typography>
                        <IconButton onClick={closeOrderDataSheet} size="small">
                            <X size={16} strokeWidth={2} />
                        </IconButton>
                    </Box>

                    <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
                        {isForm && (
                            <OrderData
                                ref={orderDataRef}
                                isForm={isForm}
                                editMode={isForm}
                                handleSubmit={handleSubmit}
                                id={id}
                                setOrderNumber={setOrderNumber}
                                status={orderState.status}
                                setOrderStatus={(status) => {
                                    if (status && status !== orderState.status) {
                                        setOrderState(prev => ({ ...prev, status }));
                                    }
                                }}
                            />
                        )}
                    </Box>

                    <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end', gap: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                        <Button variant="outlined" onClick={closeOrderDataSheet}>Cancelar</Button>
                        <Button variant="contained" startIcon={<Save size={15} strokeWidth={2} />} onClick={handleSave}>Salvar</Button>
                    </Box>
                </Drawer>
            </Box>
        </PageScaffold>
    );
}
